import { useRef } from 'react';
import { Avatar, Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import { useEngagementManager } from 'dashboard409a/hooks/useEngagementManager';
import { getInitials } from 'utilities';
import { EngagementManagerCellProps } from './cells.props';
import { PersonSearchPopover } from './personSearchPopover';

const EngagementManagerCell = ({ valuationRow, onEngagementManagerUpdate }: EngagementManagerCellProps) => {
  const cellRef = useRef<HTMLDivElement>(null);

  const {
    anchorEl,
    searchTerm,
    searching,
    engagementManagers,
    selectedManagers,
    open,
    popoverId,
    handleClick,
    handleClose,
    handleToggleManager,
    setSearchTerm,
    isUpdating,
  } = useEngagementManager({ valuationRow, onEngagementManagerUpdate });

  if (valuationRow.is_loading_additional_data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Box>
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </Box>
    );
  }

  const currentManager = selectedManagers[0] ?? null;

  const cellContent = currentManager ? (
    <Box
      ref={cellRef}
      data-activatable="true"
      onClick={handleClick}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}>
      <Box sx={{ position: 'relative', width: 28, height: 28, flexShrink: 0 }}>
        <Avatar
          alt={currentManager.name}
          src={currentManager.imageSrc && currentManager.imageSrc !== '-' ? currentManager.imageSrc : undefined}
          sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
          {getInitials({ name: currentManager.name })}
        </Avatar>
        {isUpdating && (
          <CircularProgress size={28} sx={{ position: 'absolute', top: 0, left: 0, color: 'primary.main' }} />
        )}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
        {currentManager.name}
      </Typography>
    </Box>
  ) : (
    <Box
      data-activatable="true"
      onClick={handleClick}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}>
      {isUpdating ? (
        <CircularProgress size={16} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      )}
    </Box>
  );

  const results = engagementManagers.map(em => ({
    name: em.name,
    targetRecordId: em.targetRecordId,
    imageSrc: em.imageSrc,
  }));

  return (
    <>
      {cellContent}
      <PersonSearchPopover
        open={open}
        anchorEl={anchorEl}
        popoverId={popoverId}
        onClose={handleClose}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        searching={searching}
        results={results}
        currentSelections={selectedManagers}
        onToggleSelection={handleToggleManager}
        placeholder="Search for engagement manager..."
        minWidth={300}
      />
    </>
  );
};

export default EngagementManagerCell;
